import { motion } from 'framer-motion';
import { BookOpen, TrendingUp } from 'lucide-react';
import type { ExperienceMode } from '@/hooks/usePreferredSport';

interface ExperienceToggleProps {
  mode: ExperienceMode;
  onChange: (mode: ExperienceMode) => void;
  compact?: boolean;
}

const modes = [
  {
    id: 'basics' as ExperienceMode,
    icon: BookOpen,
    label: 'Learn the Basics',
    shortLabel: 'Basics',
    description: 'Start from scratch — rules, positions, and how the game works',
  },
  {
    id: 'improve' as ExperienceMode,
    icon: TrendingUp,
    label: 'Improve My Game',
    shortLabel: 'Improve',
    description: 'Sharpen decision-making, formations, and game awareness',
  },
];

export function ExperienceToggle({ mode, onChange, compact = false }: ExperienceToggleProps) {
  if (compact) {
    return (
      <div className="flex items-center bg-muted rounded-xl p-1 gap-1">
        {modes.map(m => {
          const active = mode === m.id;
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              onClick={() => onChange(m.id)}
              className={`relative flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-display font-bold transition-all ${
                active ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {active && (
                <motion.div
                  layoutId="exp-toggle-bg"
                  className="absolute inset-0 bg-primary rounded-lg"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" />
                {m.shortLabel}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-center">
        <h3 className="font-display font-extrabold text-xl">Choose your experience</h3>
        <p className="text-muted-foreground font-body text-sm mt-1">
          You can switch anytime
        </p>
      </div>

      <div className="space-y-2.5">
        {modes.map(m => {
          const active = mode === m.id;
          const Icon = m.icon;
          return (
            <motion.button
              key={m.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => onChange(m.id)}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                active
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                  : 'border-border bg-card hover:border-primary/30'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                active ? 'bg-primary/15' : 'bg-muted'
              }`}>
                <Icon className={`w-6 h-6 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-base">{m.label}</p>
                <p className="text-xs text-muted-foreground font-body mt-0.5">{m.description}</p>
              </div>
              {active && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0"
                >
                  <svg className="w-3.5 h-3.5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
